import {
  Box,
  Button,
  Flex,
  Text,
  useDisclosure,
  useOutsideClick,
  useToast
} from "@chakra-ui/react"
import { useAtom } from "jotai"
import React, { useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Paths } from "../../../common/commonConstants"
import { loginModalOpenAtom, userDetailsAtom, userProfileDataAtom } from "../../Menu/menu.store"
import { clearAuthData } from "../../Menu/menu.support"
import { useLogoutUserMutation } from "../../../type-and-hooks"
import { LoginModal } from "./LoginModal"
import { ProfileModal } from "./ProfileModal"

const UserIcon = (): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

const SignOutIcon = (): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
)

const getUserInitials = (firstName?: string, lastName?: string): string => {
  const f = firstName?.[0]?.toUpperCase() ?? ""
  const l = lastName?.[0]?.toUpperCase() ?? ""
  return f + l || "?"
}

const pillStyle = {
  bg: "white",
  borderRadius: "full",
  color: "#861F41",
  fontWeight: "600",
  fontSize: "14px",
  height: "32px",
  px: "16px",
  _hover: { bg: "rgba(255,255,255,0.9)" }
}

interface NewAuthHeaderProps {
  onNavigate: () => void
  ml?: string | number
}

export const NewAuthHeader = ({ onNavigate, ml = "22px" }: NewAuthHeaderProps): JSX.Element => {
  const navigate = useNavigate()
  const toast = useToast()
  const [userDetails, setUserDetails] = useAtom(userDetailsAtom)
  const [, setUserProfileData] = useAtom(userProfileDataAtom)
  const [logoutAPICall, { loading: logoutLoading }] = useLogoutUserMutation()

  const [loginModalFromAtom, setLoginModalFromAtom] = useAtom(loginModalOpenAtom)
  const { isOpen: loginOpen, onOpen: openLogin, onClose: closeLogin } = useDisclosure()
  const { isOpen: profileOpen, onOpen: openProfile, onClose: closeProfile } = useDisclosure()
  const [dropdownOpen, setDropdownOpen] = React.useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useOutsideClick({ ref: dropdownRef, handler: () => setDropdownOpen(false) })

  const handleLogout = (): void => {
    setDropdownOpen(false)
    if (userDetails?._id) {
      logoutAPICall({ variables: { userId: userDetails._id } })
        .finally(() => {
          clearAuthData()
          setUserDetails(null)
          setUserProfileData({})
          navigate("/")
        })
    }
  }

  const handleMyFMPs = (): void => {
    onNavigate()
    navigate(Paths.FMP.MY_FMP)
  }

  // Open modal when triggered externally (e.g. session expiry)
  useEffect(() => {
    if (loginModalFromAtom) {
      openLogin()
      setLoginModalFromAtom(false)
    }
  }, [loginModalFromAtom])

  const handleLoginSuccess = (): void => {
    closeLogin()
  }

  const initials = getUserInitials(userDetails?.first_name, userDetails?.last_name)

  // ── Logged out ──
  if (!userDetails?._id) {
    return (
      <>
        <Button {...pillStyle} mb="28px" ml={ml} onClick={openLogin}>
          Log In
        </Button>
        <LoginModal isOpen={loginOpen} onClose={closeLogin} onSuccess={handleLoginSuccess} />
      </>
    )
  }

  // ── Logged in ──
  return (
    <>
      <Flex align="center" gap="3" mb="28px" ml={ml} position="relative">
        {/* My FMPs pill with plus icon */}
        <Button {...pillStyle} onClick={handleMyFMPs}>
          My FMPs
        </Button>

        {/* User pill */}
        <Box ref={dropdownRef} position="relative">
          <Button
            bg="white"
            borderRadius="full"
            height="32px"
            px="3"
            display="flex"
            alignItems="center"
            gap="2"
            _hover={{ bg: "rgba(255,255,255,0.9)" }}
            onClick={() => setDropdownOpen(v => !v)}
          >
            {/* Avatar */}
            <Flex
              w="22px" h="22px" borderRadius="full" bg="#861F41"
              align="center" justify="center" flexShrink={0}
            >
              <Text color="white" fontSize="9px" fontWeight="700" lineHeight="1">{initials}</Text>
            </Flex>
            <Text fontSize="13px" fontWeight="600" color="#861F41" maxW="100px" noOfLines={1}>
              {userDetails.first_name ?? "Account"}
            </Text>
            {/* Chevron */}
            <Text color="#861F41" fontSize="10px" mt="1px" style={{ transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>
              ▾
            </Text>
          </Button>

          {/* Dropdown */}
          {dropdownOpen && (
            <Box
              position="absolute"
              top="calc(100% + 8px)"
              right="0"
              bg="#861F41"
              borderRadius="10px"
              boxShadow="0 4px 20px rgba(0,0,0,0.25)"
              minW="210px"
              zIndex={200}
              overflow="hidden"
            >
              {/* Dropdown header */}
              <Box bg="rgba(0,0,0,0.15)" px="4" py="3" borderBottom="1px solid rgba(255,255,255,0.15)">
                <Text fontWeight="600" fontSize="14px" color="white" noOfLines={1}>
                  {userDetails.first_name} {userDetails.last_name}
                </Text>
                <Text fontSize="12px" color="rgba(255,255,255,0.75)" noOfLines={1}>{userDetails.email_address}</Text>
              </Box>

              {/* My Profile */}
              <Button
                variant="unstyled"
                w="100%"
                borderRadius="0"
                _hover={{ bg: "rgba(255,255,255,0.15)" }}
                onClick={() => { setDropdownOpen(false); openProfile() }}
              >
                <Flex align="center" px="4" py="3" gap="3">
                  <Flex w="20px" justify="center" flexShrink={0}><UserIcon /></Flex>
                  <Text color="white" fontSize="14px" fontWeight="400">My Profile</Text>
                </Flex>
              </Button>

              <Box h="1px" bg="rgba(255,255,255,0.15)" mx="4" />

              {/* Sign out */}
              <Button
                variant="unstyled"
                w="100%"
                borderRadius="0"
                _hover={{ bg: "rgba(255,255,255,0.15)" }}
                isLoading={logoutLoading}
                onClick={handleLogout}
              >
                <Flex align="center" px="4" py="3" gap="3">
                  <Flex w="20px" justify="center" flexShrink={0}><SignOutIcon /></Flex>
                  <Text color="white" fontSize="14px" fontWeight="400">Sign out</Text>
                </Flex>
              </Button>
            </Box>
          )}
        </Box>
      </Flex>

      <ProfileModal isOpen={profileOpen} onClose={closeProfile} />
    </>
  )
}
